package top.wcpe.allincore.dsl.ir.model

import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyList
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyOrderedMap
import top.wcpe.allincore.dsl.ir.model.overlay.ContentRef
import kotlin.jvm.JvmOverloads
import kotlin.jvm.JvmStatic

/**
 * UI 元素节点 IR。
 *
 * 字段：
 * - `kind` —— 元素类型（如 box / text / button），不能为 null。
 * - `id` —— 元素 id，不能为 null。
 * - `props` —— 属性绑定表，按插入顺序保留（LinkedHashMap）；构造后不可变。
 * - `animationRefs` —— 动画引用表，按插入顺序保留；构造后不可变。
 * - `children` —— 子节点列表；构造后不可变。
 * - `canvasData` —— canvas 元素专属数据；非 canvas 节点为 null。
 * - `sourceExpr` —— M6 数据驱动容器：求值产生数据源（List/Map）的表达式 blob；
 *   null 表示该节点不是数据驱动容器。
 * - `sourceParams` —— M6 数据驱动容器形参名列表（含 `_` 丢弃名）；
 *   List 数据源 size=1，Map 数据源 size=2；非数据驱动容器为空列表。
 */
class UiNode(
    kind: String?,
    id: String?,
    props: Map<String, UiBinding>?,
    animationRefs: Map<String, String>?,
    children: List<UiNode>?,
    /** canvas 元素专属数据；非 canvas 节点为 null。M11.1 引入。 */
    val canvasData: UiCanvasData?,
    sourceExpr: ByteArray?,
    sourceParams: List<String>?,
    /** Toast 富内容引用；非 toast 节点通常为 null。 */
    val contentRef: ContentRef?,
    selectorExpr: ByteArray?,
    iterableExpr: ByteArray?,
    forVar1: String?,
    val forVar2: String?,
    val forOverMap: Boolean,
    keyExpr: ByteArray?,
    /**
     * 布局动画（FLIP）标记位（v29）。
     *
     * 默认 false=不参与布局动画；true=运行时在 Taffy 重算导致该元素布局盒位置变化时，
     * 自动用过渡把变化补间出来（标记式 opt-in）。仅为 IR 模型标记，runtime 求解为后续里程碑。
     */
    val animateLayout: Boolean,
) {

    val kind: String = kind ?: throw NullPointerException("kind")
    val id: String = id ?: throw NullPointerException("id")
    val props: Map<String, UiBinding>
    val animationRefs: Map<String, String>
    val children: List<UiNode>

    // M6 数据驱动容器：null 表示普通容器；非 null 时为求值后产生 List/Map 的表达式 blob。
    private val sourceExprInternal: ByteArray?

    // M6 数据驱动容器形参名列表；空列表表示无（普通容器）。
    val sourceParams: List<String>

    // RFC-RECOMPOSITION：条件分支节点的 selector 表达式 blob。仅 KIND_CONDITIONAL 节点非 null。
    private val selectorExprInternal: ByteArray?

    // 元素树位置 for 循环（v26）：求值产生可迭代源的表达式 blob。仅 KIND_FOR 节点非 null。
    private val iterableExprInternal: ByteArray?

    /** for 第一个循环变量名（List/Range 形态为 item，Map 形态为 key）；仅 KIND_FOR 节点非 null。 */
    val forVar1: String?

    // for 显式 key 表达式 blob（v28）。仅 KIND_FOR 节点允许携带。
    private val keyExprInternal: ByteArray?

    /**
     * 视觉 modifier 链（v32）。
     *
     * 按 DSL 声明顺序保存；空列表表示该节点没有显式视觉 modifier。迁移期旧构造器默认空链。
     */
    private var modifiersInternal: List<UiModifier> = emptyList()

    init {
        val p = props ?: throw NullPointerException("props")
        val ar = animationRefs ?: throw NullPointerException("animationRefs")
        val ch = children ?: throw NullPointerException("children")
        // 防御式拷贝 + 不可变包装；保留插入顺序
        this.props = ReadOnlyOrderedMap(LinkedHashMap(p))
        val copiedRefs = LinkedHashMap<String, String>(ar.size)
        for ((key, value) in ar) {
            copiedRefs[key ?: throw NullPointerException("animationRef key")] =
                value ?: throw NullPointerException("animationRef value")
        }
        this.animationRefs = ReadOnlyOrderedMap(copiedRefs)
        this.children = ReadOnlyList(ArrayList(ch))
        // M6-c：sourceExpr 与 sourceParams 必须同时存在或同时缺失，防止半构造的数据驱动节点
        val hasSourceBytes = sourceExpr != null && sourceExpr.isNotEmpty()
        val hasParams = sourceParams != null && sourceParams.isNotEmpty()
        require(hasSourceBytes == hasParams) {
            "sourceExpr 与 sourceParams 必须同时存在或同时不存在（kind=" + this.kind + ", id=" + this.id + "）"
        }
        // sourceExpr：null 保留为 null；非 null 走防御式拷贝
        this.sourceExprInternal = sourceExpr?.copyOf()
        // sourceParams：null/空 统一为 emptyList()；非空校验内部不可为 null 后冻结
        if (sourceParams == null || sourceParams.isEmpty()) {
            this.sourceParams = emptyList()
        } else {
            val copied = ArrayList<String>(sourceParams.size)
            for (item in sourceParams) {
                copied.add(item ?: throw NullPointerException("sourceParam"))
            }
            this.sourceParams = ReadOnlyList(copied)
        }
        // RFC-RECOMPOSITION：仅 KIND_CONDITIONAL 节点允许携带 selectorExpr。
        val hasSelectorBytes = selectorExpr != null && selectorExpr.isNotEmpty()
        require(!(hasSelectorBytes && KIND_CONDITIONAL != this.kind)) {
            "selectorExpr 仅允许在 KIND_CONDITIONAL 节点上设置（kind=" + this.kind + ", id=" + this.id + "）"
        }
        require(!(KIND_CONDITIONAL == this.kind && !hasSelectorBytes)) {
            "KIND_CONDITIONAL 节点必须携带非空 selectorExpr（id=" + this.id + "）"
        }
        this.selectorExprInternal = selectorExpr?.copyOf()
        // $for（v26）：仅 KIND_FOR 节点允许携带 iterableExpr / forVar1 / forVar2 / forOverMap。
        val hasIterableBytes = iterableExpr != null && iterableExpr.isNotEmpty()
        val isForKind = KIND_FOR == this.kind
        require(!(hasIterableBytes && !isForKind)) {
            "iterableExpr 仅允许在 KIND_FOR 节点上设置（kind=" + this.kind + ", id=" + this.id + "）"
        }
        require(!(isForKind && !hasIterableBytes)) {
            "KIND_FOR 节点必须携带非空 iterableExpr（id=" + this.id + "）"
        }
        if (isForKind) {
            require(!(forVar1 == null || forVar1.trim().isEmpty())) {
                "KIND_FOR 节点必须携带非空 forVar1（id=" + this.id + "）"
            }
            // $for 与数据驱动容器 / conditional 互斥（selectorExpr 已由上方校验保证仅 $cond 才有）。
            require(!hasSourceBytes) { "KIND_FOR 节点不得同时是数据驱动容器（id=" + this.id + "）" }
        } else {
            require(!(forVar1 != null || forVar2 != null || forOverMap)) {
                "非 KIND_FOR 节点不得携带 for 循环变量（kind=" + this.kind + ", id=" + this.id + "）"
            }
        }
        this.iterableExprInternal = iterableExpr?.copyOf()
        this.forVar1 = forVar1
        // keyExpr：仅 KIND_FOR 节点允许，但 KIND_FOR 也可以不携带（null = 自动 key 提取）。
        val hasKeyExprBytes = keyExpr != null && keyExpr.isNotEmpty()
        require(!(hasKeyExprBytes && !isForKind)) {
            "keyExpr 仅允许在 KIND_FOR 节点上设置（kind=" + this.kind + ", id=" + this.id + "）"
        }
        this.keyExprInternal = if (hasKeyExprBytes) keyExpr!!.copyOf() else null
    }

    /**
     * 完整构造器（v32）：在 v29 标记位之后追加有序视觉 modifier 链。
     */
    constructor(
        kind: String?,
        id: String?,
        props: Map<String, UiBinding>?,
        animationRefs: Map<String, String>?,
        children: List<UiNode>?,
        canvasData: UiCanvasData?,
        sourceExpr: ByteArray?,
        sourceParams: List<String>?,
        contentRef: ContentRef?,
        selectorExpr: ByteArray?,
        iterableExpr: ByteArray?,
        forVar1: String?,
        forVar2: String?,
        forOverMap: Boolean,
        keyExpr: ByteArray?,
        animateLayout: Boolean,
        modifiers: List<UiModifier>?,
    ) : this(
        kind, id, props, animationRefs, children, canvasData, sourceExpr, sourceParams,
        contentRef, selectorExpr, iterableExpr, forVar1, forVar2, forOverMap, keyExpr, animateLayout,
    ) {
        this.modifiersInternal = copyModifiers(modifiers)
    }

    /**
     * 兼容构造器：在 RFC-RECOMPOSITION selectorExpr 之上追加 v26 引入的 $for 段
     * （iterableExpr / forVar1 / forVar2 / forOverMap）。委托到含 animateLayout 的最长构造器
     * 并传 `animateLayout=false`，保留所有既有调用点。
     */
    @JvmOverloads
    constructor(
        kind: String?,
        id: String?,
        props: Map<String, UiBinding>?,
        animationRefs: Map<String, String>?,
        children: List<UiNode>?,
        canvasData: UiCanvasData?,
        sourceExpr: ByteArray?,
        sourceParams: List<String>?,
        contentRef: ContentRef?,
        selectorExpr: ByteArray?,
        iterableExpr: ByteArray?,
        forVar1: String?,
        forVar2: String?,
        forOverMap: Boolean,
        keyExpr: ByteArray? = null,
    ) : this(
        kind, id, props, animationRefs, children, canvasData, sourceExpr, sourceParams,
        contentRef, selectorExpr, iterableExpr, forVar1, forVar2, forOverMap, keyExpr, false,
    )

    /**
     * RFC-RECOMPOSITION 兼容构造器：不带 $for 段，委托到最长构造器并传 null/null/null/false。
     */
    @JvmOverloads
    constructor(
        kind: String?,
        id: String?,
        props: Map<String, UiBinding>?,
        animationRefs: Map<String, String>?,
        children: List<UiNode>?,
        canvasData: UiCanvasData?,
        sourceExpr: ByteArray?,
        sourceParams: List<String>?,
        contentRef: ContentRef? = null,
        selectorExpr: ByteArray? = null,
    ) : this(
        kind, id, props, animationRefs, children, canvasData, sourceExpr, sourceParams,
        contentRef, selectorExpr, null, null, null, false, null,
    )

    constructor(
        kind: String?,
        id: String?,
        props: Map<String, UiBinding>?,
        animationRefs: Map<String, String>?,
        children: List<UiNode>?,
        canvasData: UiCanvasData?,
    ) : this(kind, id, props, animationRefs, children, canvasData, null, emptyList())

    constructor(
        kind: String?,
        id: String?,
        props: Map<String, UiBinding>?,
        animationRefs: Map<String, String>?,
        children: List<UiNode>?,
    ) : this(kind, id, props, animationRefs, children, null, null, emptyList())

    constructor(kind: String?, id: String?, props: Map<String, UiBinding>?, children: List<UiNode>?) :
        this(kind, id, props, emptyMap(), children, null, null, emptyList())

    /** 是否为 M6 数据驱动容器。 */
    fun hasSource(): Boolean = sourceExprInternal != null && sourceExprInternal.isNotEmpty()

    /**
     * 返回数据源表达式 blob 的拷贝；调用方应先用 [hasSource] 判定。
     * 非数据驱动容器返回 null。
     * 复刻原 Java `getSourceExpr()`：暴露为属性 `sourceExpr`，保留 private backing + 防御拷贝。
     */
    val sourceExpr: ByteArray?
        get() = sourceExprInternal?.copyOf()

    /**
     * RFC-RECOMPOSITION：是否为条件分支容器节点。
     * 等价于 `KIND_CONDITIONAL == getKind()`，便于 IrInterpreter 快速分派。
     * 复刻原 Java `isConditional()`：改为计算属性以保下游属性语法访问。
     */
    val isConditional: Boolean
        get() = KIND_CONDITIONAL == kind

    /**
     * 返回 selector 表达式 blob 的拷贝；非 conditional 节点返回 null。
     * 调用方应先用 [isConditional] 判定。
     * 复刻原 Java `getSelectorExpr()`：暴露为属性 `selectorExpr`，保留 private backing + 防御拷贝。
     */
    val selectorExpr: ByteArray?
        get() = selectorExprInternal?.copyOf()

    /**
     * 元素树位置 for 循环（v26）：是否为 for 容器节点。
     * 等价于 `KIND_FOR == getKind()`，便于 IrInterpreter 快速分派。
     * 复刻原 Java `isFor()`：改为计算属性以保下游属性语法访问。
     */
    val isFor: Boolean
        get() = KIND_FOR == kind

    /**
     * 返回 for 可迭代表达式 blob 的拷贝；非 for 节点返回 null。调用方应先用 [isFor] 判定。
     * 复刻原 Java `getIterableExpr()`：暴露为属性 `iterableExpr`，保留 private backing + 防御拷贝。
     */
    val iterableExpr: ByteArray?
        get() = iterableExprInternal?.copyOf()

    /** 是否声明了 for 第二个循环变量（index 或 value）。 */
    fun hasForVar2(): Boolean = forVar2 != null

    /**
     * for 是否遍历 Map（var1=key, var2=value）；false 为 List/Range 形态。
     * 复刻原 Java `isForOverMap()`：改为计算属性以保下游属性语法访问（读构造入参 [forOverMap]）。
     */
    val isForOverMap: Boolean
        get() = forOverMap

    /** 是否携带显式 key 表达式（v28）；false 时运行期使用自动 key 提取。 */
    fun hasKeyExpr(): Boolean = keyExprInternal != null

    /**
     * 返回 for 显式 key 表达式 blob 的拷贝；未设置时返回 null。调用方应先用 [hasKeyExpr] 判定。
     * 复刻原 Java `getKeyExpr()`：暴露为属性 `keyExpr`，保留 private backing + 防御拷贝。
     */
    val keyExpr: ByteArray?
        get() = keyExprInternal?.copyOf()

    /**
     * 布局动画（FLIP）标记位（v29）；true 表示该元素参与布局动画。
     * 复刻原 Java `isAnimateLayout()`：改为计算属性以保下游属性语法访问（读构造入参 [animateLayout]）。
     */
    val isAnimateLayout: Boolean
        get() = animateLayout

    /** 视觉 modifier 链（v32）；按 DSL 声明顺序返回，不可变。 */
    val modifiers: List<UiModifier>
        get() = modifiersInternal

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UiNode) return false
        return kind == other.kind &&
            id == other.id &&
            props == other.props &&
            animationRefs == other.animationRefs &&
            children == other.children &&
            canvasData == other.canvasData &&
            sourceExprInternal.contentEquals(other.sourceExprInternal) &&
            sourceParams == other.sourceParams &&
            contentRef == other.contentRef &&
            selectorExprInternal.contentEquals(other.selectorExprInternal) &&
            iterableExprInternal.contentEquals(other.iterableExprInternal) &&
            forVar1 == other.forVar1 &&
            forVar2 == other.forVar2 &&
            forOverMap == other.forOverMap &&
            keyExprInternal.contentEquals(other.keyExprInternal) &&
            animateLayout == other.animateLayout &&
            modifiers == other.modifiers
    }

    override fun hashCode(): Int {
        // 复刻 Objects.hash(...非字节字段...) 再逐 byte[] 追加 31 倍累乘。
        var h = 1
        h = 31 * h + kind.hashCode()
        h = 31 * h + id.hashCode()
        h = 31 * h + props.hashCode()
        h = 31 * h + animationRefs.hashCode()
        h = 31 * h + children.hashCode()
        h = 31 * h + (canvasData?.hashCode() ?: 0)
        h = 31 * h + sourceParams.hashCode()
        h = 31 * h + (contentRef?.hashCode() ?: 0)
        h = 31 * h + (forVar1?.hashCode() ?: 0)
        h = 31 * h + (forVar2?.hashCode() ?: 0)
        h = 31 * h + forOverMap.hashCode()
        h = 31 * h + animateLayout.hashCode()
        h = 31 * h + modifiers.hashCode()
        h = 31 * h + sourceExprInternal.contentHashCode()
        h = 31 * h + selectorExprInternal.contentHashCode()
        h = 31 * h + iterableExprInternal.contentHashCode()
        h = 31 * h + keyExprInternal.contentHashCode()
        return h
    }

    override fun toString(): String {
        val sb = StringBuilder()
        sb.append("UiNode{").append(kind).append(" #").append(id)
            .append(", props=").append(props.size)
            .append(", animationRefs=").append(animationRefs.keys)
            .append(", children=").append(children.size)
        if (hasSource()) {
            sb.append(", source=").append(sourceExprInternal!!.size).append("B")
                .append(", sourceParams=").append(sourceParams)
        }
        if (selectorExprInternal != null) {
            sb.append(", selector=").append(selectorExprInternal.size).append("B")
        }
        if (isFor) {
            sb.append(", for=").append(iterableExprInternal!!.size).append("B")
                .append(", var1=").append(forVar1)
            if (forVar2 != null) {
                sb.append(", var2=").append(forVar2)
            }
            sb.append(", overMap=").append(forOverMap)
            if (keyExprInternal != null) {
                sb.append(", key=").append(keyExprInternal.size).append("B")
            }
        }
        if (contentRef != null) {
            sb.append(", contentRef=").append(simpleName(contentRef))
        }
        if (animateLayout) {
            sb.append(", animateLayout=true")
        }
        if (modifiers.isNotEmpty()) {
            sb.append(", modifiers=").append(modifiers)
        }
        sb.append("}")
        return sb.toString()
    }

    companion object {
        /**
         * RFC-RECOMPOSITION：条件分支节点的固定 kind。
         *
         * `$cond` 前缀避免与现有 element kind（box / text / button / column / ...）冲突。
         * IrInterpreter 检测到此 kind 时优先走 conditional 容器路径，children 被解释为 branch
         * templates；selectorExpr 求值结果是命中的 children 索引（-1 = 无命中且无 else）。
         */
        const val KIND_CONDITIONAL = "\$cond"

        /**
         * 元素树位置 for 循环重组节点的固定 kind。
         *
         * `$for` 前缀同样避免与现有 element kind 冲突。IrInterpreter 检测到此 kind 时
         * 优先走 for 容器路径：children 被解释为单根循环体模板，iterableExpr 求值产生可迭代源
         * （List/Range/任意 Iterable，或 forOverMap=true 时的 Map），运行期按 key 增量 diff
         * 实例化/复用/dispose 子元素。
         */
        const val KIND_FOR = "\$for"

        /**
         * RFC-RECOMPOSITION：构造一个条件分支节点。
         *
         * kind 强制为 [KIND_CONDITIONAL]，children 解释为 branch templates，
         * selectorExpr 求值结果为命中的 children 索引。
         */
        @JvmStatic
        fun conditional(id: String?, children: List<UiNode>?, selectorExpr: ByteArray?): UiNode =
            UiNode(
                KIND_CONDITIONAL,
                id ?: throw NullPointerException("id"),
                emptyMap(),
                emptyMap(),
                children,
                null,
                null,
                emptyList(),
                null,
                selectorExpr ?: throw NullPointerException("selectorExpr"),
            )

        /**
         * 元素树位置 for 循环（v28）：构造一个 for 容器节点（可携带显式 key 表达式）。
         *
         * kind 强制为 [KIND_FOR]，children 解释为单根循环体模板，iterableExpr 求值产生可迭代源。
         */
        @JvmStatic
        @JvmOverloads
        fun forEach(
            id: String?,
            children: List<UiNode>?,
            iterableExpr: ByteArray?,
            var1: String?,
            var2: String?,
            overMap: Boolean,
            keyExpr: ByteArray? = null,
        ): UiNode =
            UiNode(
                KIND_FOR,
                id ?: throw NullPointerException("id"),
                emptyMap(),
                emptyMap(),
                children,
                null,
                null,
                emptyList(),
                null,
                null,
                iterableExpr ?: throw NullPointerException("iterableExpr"),
                var1 ?: throw NullPointerException("var1"),
                var2,
                overMap,
                keyExpr,
            )

        private fun copyModifiers(modifiers: List<UiModifier>?): List<UiModifier> {
            if (modifiers == null || modifiers.isEmpty()) {
                return emptyList()
            }
            val copied = ArrayList<UiModifier>(modifiers.size)
            for (modifier in modifiers) {
                copied.add(modifier ?: throw NullPointerException("modifier"))
            }
            return ReadOnlyList(copied)
        }

        // commonMain 无 Class.getSimpleName()，用类型分支复刻 ContentRef 子类简单名。
        private fun simpleName(ref: ContentRef): String = when (ref) {
            is ContentRef.Text -> "Text"
            is ContentRef.Inline -> "Inline"
            is ContentRef.Component -> "Component"
            else -> "ContentRef"
        }
    }
}
